/**
 * ================================================================
 * CUSTOM HOOK: useSafeNavigate
 * ================================================================
 *
 * @hook             useSafeNavigate
 * @description      Provides a startTransition-wrapped navigate to avoid React 18 warning:
 *                   "A component suspended while responding to synchronous input."
 * @category         UI Hook
 *
 * @returns
 * - navigateSafe: (...args) => void - navigate wrapped in startTransition
 * - navigate: Return of useNavigate for advanced usage
 *
 * @usage
 * // const { navigateSafe } = useSafeNavigate();
 * // <Button onClick={() => navigateSafe('/reports/ledger')} />
 * ================================================================
 */

import { startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavigateOptions, To } from 'react-router-dom';

/**
 * Wraps useNavigate to run inside startTransition to prevent UI replacement
 * during synchronous input when lazy routes/components suspend.
 */
export function useSafeNavigate() {
  const navigate = useNavigate();

  function navigateSafe(to: number): void;
  function navigateSafe(to: To, options?: NavigateOptions): void;
  function navigateSafe(to: To | number, options?: NavigateOptions) {
    startTransition(() => {
      (navigate as any)(to as any, options as any);
    });
  }

  return { navigateSafe, navigate } as const;
}

/**
 * ================================================================
 * END OF HOOK: useSafeNavigate
 * ================================================================
 *
 * @exports          useSafeNavigate - Transition-safe navigation
 * @dependencies     react-router-dom useNavigate, React.startTransition
 * @usedBy           UI click handlers that cause route changes
 *
 * ================================================================
 */
